import { httpClient } from "@/lib/httpClient";
import type { User } from "@/types/user";

interface CsrfResponse {
  csrfToken: string;
}

/**
 * Auth/session API. All auth-related network calls live here so the rest of the
 * app depends on this interface, not on endpoint URLs. Sign-out is intentionally
 * NOT here: it is a full-page form POST (so the browser follows the cross-origin
 * Microsoft logout redirect) — see `components/UserMenu`.
 */
export const authService = {
  /** The signed-in user, or null if the session has no identity yet. */
  async getCurrentUser(): Promise<User | null> {
    try {
      return await httpClient.get<User>("/api/me");
    } catch {
      // On the HTTPS local domain, hard-redirect unauthenticated users to the
      // login page and return a never-resolving promise so the caller's useEffect
      // never calls setStatus("ready") — preventing the AppShell from flashing
      // before the navigation completes. Other dev hosts (e.g. localhost) and any
      // transient failure fall through to `null`; AuthProvider then renders the
      // <Navigate to="/login"> redirect. Guard against already being on /login to
      // avoid an infinite loop.
      if (
        window.location.hostname === "opshub.birzulis.test" &&
        window.location.pathname !== "/login"
      ) {
        window.location.assign("/login");
        return new Promise<User | null>(() => {});
      }

      // Unauthenticated or transient: callers treat null as "no user".
      return null;
    }
  },

  /** A CSRF token for state-changing form submissions (e.g. sign-out). */
  async getCsrfToken(): Promise<string | null> {
    try {
      const { csrfToken } = await httpClient.get<CsrfResponse>("/api/csrf");
      return csrfToken;
    } catch {
      return null;
    }
  },

  /** Path the sign-out form posts to. */
  signOutPath: "/auth/signout",
};
